import { sanitizeAgentId } from './addExisting';

/**
 * Where a Hermes agent can be talked to. The board can already open an n8n workflow from its card;
 * this is the same idea for the agents, whose equivalent of an editor is their Slack conversation.
 *
 * Keyed by the agent's display name because that is the identity the Slack bot shares verbatim, and
 * because it follows the agent when its work moves to another machine — a node id would not.
 *
 * This map is a stopgap. The durable home is the connector reporting its own bot id as a node label,
 * the way it already reports its skills; hermes carries a slackBotUserId field for exactly this and
 * nothing consumes it yet.
 */
const SLACK_MEMBER_BY_AGENT: Record<string, string> = {
  'SkinSeoul Category Agent': 'U0BJCPXDSP7',
  'SkinSeoul Developer Agent': 'U0BJ0N5KT1V',
  'SkinSeoul Founder Agent': 'U0BJJ107ECR',
  'SkinSeoul Marketing Agent': 'U0BJCQ33B0V',
  'SkinSeoul Operations Agent': 'U0BJG39PSAE',
  'SkinSeoul Partnership Agent': 'U0BJJ0Y46AD',
};

/** The agent's Slack member id, or null when it has none — an unknown name never guesses. */
export const slackMemberIdOf = (agentName: string | null): string | null => {
  if (agentName === null) {
    return null;
  }
  return SLACK_MEMBER_BY_AGENT[agentName] ?? null;
};

/** A deep link that opens the conversation with this agent, or null when it has no Slack identity. */
export const slackAgentDmUrl = (agentName: string | null, teamId: string): string | null => {
  const memberId = slackMemberIdOf(agentName);
  return memberId === null ? null : `https://slack.com/app_redirect?channel=${memberId}&team=${teamId}`;
};

/** A catalogue skill, narrowed to what naming its authoring node needs. */
interface SkillOrigin {
  skillId: string;
  originNodeId: string | null;
}

/**
 * The node that authored the skill behind this card. A card wired to a loop carries the loop's owner —
 * a person, or nobody — so the card's own `owner` names the node only until it joins one.
 */
export const owningNodeIdOf = (cardId: string, skills: readonly SkillOrigin[], cardOwner: string): string =>
  skills.find((skill) => sanitizeAgentId(`hermes-${skill.skillId}`) === cardId)?.originNodeId ?? cardOwner;
